/**
 * Password Validation Utility
 * Validates passwords against AWS Cognito requirements:
 * - Minimum 8 characters
 * - At least 1 uppercase letter
 * - At least 1 lowercase letter
 * - At least 1 digit
 * - At least 1 special character (symbol)
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong';
}

/**
 * Validate password against Cognito password policy requirements
 * @param password - The password to validate
 * @returns Validation result with isValid flag, error messages, and strength rating
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];

  // Check minimum length (8 characters)
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters');
  }

  // Check for at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  // Check for at least one lowercase letter
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  // Check for at least one digit
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  // Check for at least one special character (non-alphanumeric)
  if (!/[^A-Za-z0-9]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  const isValid = errors.length === 0;
  const strength = calculateStrength(password, isValid);

  return { isValid, errors, strength };
}

/**
 * Calculate password strength based on length, complexity, and entropy
 * @param password - The password to evaluate
 * @param meetsRequirements - Whether password meets basic requirements
 * @returns Strength rating: 'weak', 'medium', or 'strong'
 */
function calculateStrength(
  password: string,
  meetsRequirements: boolean
): 'weak' | 'medium' | 'strong' {
  // If doesn't meet basic requirements, automatically weak
  if (!meetsRequirements) {
    return 'weak';
  }

  let score = 0;

  // Length scoring
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (password.length >= 16) score += 1;
  if (password.length >= 20) score += 1;

  // Character variety scoring
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasDigits = /[0-9]/.test(password);
  const hasSpecialChars = /[^A-Za-z0-9]/.test(password);

  if (hasUppercase) score += 1;
  if (hasLowercase) score += 1;
  if (hasDigits) score += 1;
  if (hasSpecialChars) score += 1;

  // Additional complexity checks
  const multipleUppercase = (password.match(/[A-Z]/g) || []).length >= 2;
  const multipleDigits = (password.match(/[0-9]/g) || []).length >= 2;
  const multipleSpecialChars = (password.match(/[^A-Za-z0-9]/g) || []).length >= 2;

  if (multipleUppercase) score += 1;
  if (multipleDigits) score += 1;
  if (multipleSpecialChars) score += 1;

  // Entropy check - no repeated characters
  const uniqueChars = new Set(password.split('')).size;
  const entropyRatio = uniqueChars / password.length;

  if (entropyRatio >= 0.7) score += 2; // High entropy
  else if (entropyRatio >= 0.5) score += 1; // Medium entropy

  // Common password patterns (penalize)
  const commonPatterns = [
    /password/i,
    /welcome/i,
    /admin/i,
    /test/i,
    /qwerty/i,
    /12345/,
    /abc/i,
  ];

  const hasCommonPattern = commonPatterns.some(pattern => pattern.test(password));
  if (hasCommonPattern) score -= 2;

  // Sequential characters (penalize)
  const hasSequential = /(?:abc|bcd|cde|def|123|234|345|456|567|678|789)/i.test(password);
  if (hasSequential) score -= 1;

  // Strength classification
  if (score >= 10) return 'strong';
  if (score >= 6) return 'medium';
  return 'weak';
}

/**
 * Get user-friendly password requirements text
 * @returns Array of requirement strings for display
 */
export function getPasswordRequirements(): string[] {
  return [
    'At least 8 characters long',
    'Contains at least one uppercase letter (A-Z)',
    'Contains at least one lowercase letter (a-z)',
    'Contains at least one number (0-9)',
    'Contains at least one special character (!@#$%^&*)',
  ];
}

/**
 * Get password strength color for UI display
 * @param strength - The strength rating
 * @returns Color code for UI (error, warning, success)
 */
export function getStrengthColor(strength: 'weak' | 'medium' | 'strong'): string {
  switch (strength) {
    case 'weak':
      return 'error'; // Red
    case 'medium':
      return 'warning'; // Yellow/Orange
    case 'strong':
      return 'success'; // Green
  }
}

/**
 * Get password strength label for UI display
 * @param strength - The strength rating
 * @returns User-friendly label
 */
export function getStrengthLabel(strength: 'weak' | 'medium' | 'strong'): string {
  switch (strength) {
    case 'weak':
      return 'Weak';
    case 'medium':
      return 'Medium';
    case 'strong':
      return 'Strong';
  }
}
